import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  EmailTemplate,
  getAllTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  toggleTemplateStatus
} from '../../api/emailTemplates';

const EmailTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllTemplates();
      setTemplates(data);
    } catch (err) {
      
      enqueueSnackbar('Failed to load email templates', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleOpenDialog = (template?: EmailTemplate) => {
    setSelectedTemplate(template || {
      _id: '',
      name: '',
      type: '',
      subject: '',
      body: '',
      active: true
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedTemplate(null);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await deleteTemplate(id);
        enqueueSnackbar('Template deleted successfully', { variant: 'success' });
        fetchTemplates();
      } catch (err) {
        
        enqueueSnackbar('Failed to delete template', { variant: 'error' });
      }
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      await toggleTemplateStatus(id);
      enqueueSnackbar('Template status updated successfully', { variant: 'success' });
      fetchTemplates();
    } catch (err) {
      
      enqueueSnackbar('Failed to update template status', { variant: 'error' });
    }
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;

    try {
      setLoading(true);
      if (selectedTemplate._id) {
        await updateTemplate(selectedTemplate._id, selectedTemplate);
        enqueueSnackbar('Template updated successfully', { variant: 'success' });
      } else {
        await createTemplate(selectedTemplate);
        enqueueSnackbar('Template created successfully', { variant: 'success' });
      }
      setIsDialogOpen(false);
      fetchTemplates();
    } catch (err) {
      
      enqueueSnackbar('Failed to save template', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Email Templates</Typography>
        <Button
          variant="contained"
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: 'rgba(59, 130, 246, 0.8)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(96, 165, 250, 0.3)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(37, 99, 235, 0.9)',
            },
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }}
        >
          Add Template
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{
        backgroundColor: 'rgba(30, 41, 59, 0.95)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: 2,
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      }}>
        <Table sx={{ color: 'white' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>Subject</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>Active</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow 
                key={template._id}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  }
                }}
              >
                <TableCell sx={{ color: 'white' }}>{template.name}</TableCell>
                <TableCell sx={{ color: 'white' }}>{template.type}</TableCell>
                <TableCell sx={{ color: 'white' }}>{template.subject}</TableCell>
                <TableCell>
                  <Switch
                    checked={template.active}
                    onChange={() => handleToggleStatus(template._id)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#60a5fa',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#3b82f6',
                      },
                    }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton 
                    onClick={() => handleOpenDialog(template)}
                    sx={{ color: '#60a5fa' }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDelete(template._id)}
                    sx={{ color: '#ef4444' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog 
        open={isDialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            color: 'white',
          }
        }}
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}>
          <DialogTitle sx={{ color: 'white' }}>
            {selectedTemplate?._id ? 'Edit Template' : 'New Template'}
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} mt={2}>
              <TextField
                label="Name"
                value={selectedTemplate?.name || ''}
                onChange={(e) =>
                  setSelectedTemplate(prev =>
                    prev ? { ...prev, name: e.target.value } : null
                  )
                }
                required
                InputLabelProps={{
                  sx: { color: 'rgba(255, 255, 255, 0.7)' }
                }}
                InputProps={{
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#60a5fa',
                    },
                    color: 'white',
                  }
                }}
              />
              <TextField
                label="Type"
                value={selectedTemplate?.type || ''}
                onChange={(e) =>
                  setSelectedTemplate(prev =>
                    prev ? { ...prev, type: e.target.value } : null
                  )
                }
                required
                InputLabelProps={{
                  sx: { color: 'rgba(255, 255, 255, 0.7)' }
                }}
                InputProps={{
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#60a5fa',
                    },
                    color: 'white',
                  }
                }}
              />
              <TextField
                label="Subject"
                value={selectedTemplate?.subject || ''}
                onChange={(e) =>
                  setSelectedTemplate(prev =>
                    prev ? { ...prev, subject: e.target.value } : null
                  )
                }
                required
                InputLabelProps={{
                  sx: { color: 'rgba(255, 255, 255, 0.7)' }
                }}
                InputProps={{
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#60a5fa',
                    },
                    color: 'white',
                  }
                }}
              />
              <TextField
                label="Body"
                value={selectedTemplate?.body || ''}
                onChange={(e) =>
                  setSelectedTemplate(prev =>
                    prev ? { ...prev, body: e.target.value } : null
                  )
                }
                multiline
                rows={6}
                required
                InputLabelProps={{
                  sx: { color: 'rgba(255, 255, 255, 0.7)' }
                }}
                InputProps={{
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#60a5fa',
                    },
                    color: 'white',
                  }
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedTemplate?.active || false}
                    onChange={(e) =>
                      setSelectedTemplate(prev =>
                        prev ? { ...prev, active: e.target.checked } : null
                      )
                    }
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#60a5fa',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#3b82f6',
                      },
                    }}
                  />
                }
                label="Active"
                sx={{ color: 'white' }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseDialog}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading}
              sx={{
                backgroundColor: 'rgba(59, 130, 246, 0.8)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(96, 165, 250, 0.3)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(37, 99, 235, 0.9)',
                },
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              }}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default EmailTemplates;
